import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, ClipboardCheck, Zap } from "lucide-react";
import { PageHeader } from "@/components/site/kit";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";
import { submitAudit } from "@/lib/api";
import { toast } from "sonner";

const AUDIT_POINTS = [
  "Mobile experience", "Page speed", "SEO", "Calls to action",
  "Contact / lead capture", "Accessibility", "Trust signals", "Overall user experience",
];

const COUNTRIES = ["United States", "United Kingdom", "Canada", "Australia", "New Zealand", "Ireland", "France", "Germany", "Netherlands", "Belgium", "Switzerland", "UAE", "Singapore", "Other"];
const INDUSTRIES = ["Small business", "Professional service", "Restaurant", "Contractor / trades", "Real estate", "Healthcare", "Consultant", "Coach", "Nonprofit", "E-commerce", "Startup", "Creator", "Other"];

const inputCls = "w-full bg-secondary px-4 py-3.5 outline-none focus:ring-1 focus:ring-primary text-foreground";

export default function FreeAudit() {
  const [form, setForm] = useState({ name: "", email: "", business_name: "", website: "", country: "United States", industry: "Small business" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await submitAudit(form);
      setDone(r);
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="free-audit-page">
      <SEO
        title="Free Website Audit — Is Your Website Costing You Customers? | Julius Mason"
        description="Get a free, personal website audit: mobile experience, page speed, SEO, calls to action, lead capture, accessibility, and trust signals — findings within 2 business days."
        path="/free-website-audit"
        lang="en"
        keywords="free website audit, website review, website analysis, improve my website"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Free Website Audit", path: "/free-website-audit" }])}
      />
      <PageHeader
        chapter="Free Website Audit"
        titleLines={["Is your website costing", "you customers?"]}
        description="Get a free website audit and discover opportunities to improve your website. I personally review every request — findings in your inbox within 2 business days, no sales call required."
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
          {/* What I review */}
          <div className="lg:col-span-5">
            <p className="overline text-primary mb-6">What I review</p>
            <div className="space-y-3">
              {AUDIT_POINTS.map((p, i) => (
                <motion.div key={p} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-3 border border-border bg-card p-4" data-testid={`audit-point-${i}`}>
                  <ClipboardCheck className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="font-medium">{p}</span>
                </motion.div>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground leading-relaxed">
              You'll get honest, specific findings — what's working, what's quietly losing you customers, and what to fix first.
            </p>
          </div>

          {/* Form / confirmation */}
          <div className="lg:col-span-7">
            {done ? (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="border border-primary/40 bg-primary/5 p-8 md:p-10" data-testid="audit-confirmation">
                <CheckCircle2 className="w-10 h-10 text-primary mb-5" />
                <h2 className="font-display text-3xl">Your audit is in the queue.</h2>
                <p className="mt-4 text-muted-foreground leading-relaxed">
                  I'll personally review <b className="text-foreground">{form.website}</b> across all eight areas and email
                  my findings to <b className="text-foreground">{form.email}</b> within 2 business days.
                </p>
                <div className="mt-8 border-t border-primary/20 pt-8">
                  <p className="overline text-primary mb-3">Want me to fix it?</p>
                  <p className="text-muted-foreground mb-6">If the audit finds issues worth fixing, I can rebuild what matters — fast, with transparent pricing.</p>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/start-project" data-testid="audit-fix-cta" className="inline-flex items-center gap-2 bg-primary text-white px-7 py-3.5 font-semibold hover:bg-primary/90 active:scale-95 transition">
                      Start My Project <Zap className="w-4 h-4" />
                    </Link>
                    <Link to="/pricing" data-testid="audit-quote-cta" className="inline-flex items-center gap-2 border border-border px-7 py-3.5 font-semibold hover:border-primary hover:text-primary active:scale-95 transition">
                      Get an Instant Estimate <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="border border-border bg-card p-8 md:p-10" data-testid="audit-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="audit-name" className="overline text-muted-foreground block mb-2">Name *</label>
                    <input id="audit-name" required value={form.name} onChange={set("name")} data-testid="audit-name" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="audit-email" className="overline text-muted-foreground block mb-2">Email *</label>
                    <input id="audit-email" required type="email" value={form.email} onChange={set("email")} data-testid="audit-email" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="audit-business" className="overline text-muted-foreground block mb-2">Business name</label>
                    <input id="audit-business" value={form.business_name} onChange={set("business_name")} data-testid="audit-business" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="audit-website" className="overline text-muted-foreground block mb-2">Website URL *</label>
                    <input id="audit-website" required type="url" placeholder="https://…" value={form.website} onChange={set("website")} data-testid="audit-website" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="audit-country" className="overline text-muted-foreground block mb-2">Country</label>
                    <select id="audit-country" value={form.country} onChange={set("country")} data-testid="audit-country" className={inputCls}>
                      {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="audit-industry" className="overline text-muted-foreground block mb-2">Industry</label>
                    <select id="audit-industry" value={form.industry} onChange={set("industry")} data-testid="audit-industry" className={inputCls}>
                      {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
                    </select>
                  </div>
                </div>
                <button type="submit" disabled={busy} data-testid="audit-submit"
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-primary text-white px-7 py-4 font-semibold hover:bg-primary/90 active:scale-[0.98] transition disabled:opacity-60">
                  {busy ? "Submitting…" : "Get My Free Website Audit"} <ArrowRight className="w-4 h-4" />
                </button>
                <p className="mt-4 text-xs text-muted-foreground text-center">
                  Free, personal, and honest. No automated PDF, no sales pressure — findings within 2 business days.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
